import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectOwnershipData,
  selectLoanData,
  selectResidenceData,
  selectIncomeData,
  selectConsentData,
  selectApplicationIds, 
} from '../../../reducers/refiApplication'
import { submitRefiApplication } from '../../../actions/refiApplicationActions'

interface DisclosureAgreementFormProps {
  vehicle?: any
  product_package?: any
}

const DisclosureAgreementForm: React.FC<DisclosureAgreementFormProps> = ({ vehicle, product_package }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  
  const ownershipData = useSelector(selectOwnershipData)
  const loanData = useSelector(selectLoanData)
  const residenceData = useSelector(selectResidenceData)
  const incomeData = useSelector(selectIncomeData)
  const consentData = useSelector(selectConsentData)
  const applicationIds = useSelector(selectApplicationIds)
  const user = useSelector((state: any) => state.session?.user)
  
  const [agreeToDisclosure, setAgreeToDisclosure] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToDisclosure(e.target.checked)
    if (e.target.checked && error) {
      setError('')
    }
  }

  const calculateMonthsFromStart = (month: string, day: string, year: string) => {
    if (!month || !year) return 0
    
    const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day) || 1)
    const today = new Date()
    
    const monthsDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + 
                       (today.getMonth() - startDate.getMonth())
    
    return Math.max(0, monthsDiff)
  }

  // Calculate years and remaining months for employer tenure
  const calculateEmployerTenure = () => {
    const totalMonths = calculateMonthsFromStart(
      incomeData.startDateMonth,
      incomeData.startDateDay,
      incomeData.startDateYear
    )
    
    return {
      years: Math.floor(totalMonths / 12),
      months: totalMonths % 12
    }
  }

  // Format DOB as YYYY-MM-DD
  const formatDOB = () => {
    if (!consentData.dobMonth || !consentData.dobDay || !consentData.dobYear) {
      return null
    }
    
    const month = consentData.dobMonth.toString().padStart(2, '0')
    const day = consentData.dobDay.toString().padStart(2, '0')
    const year = consentData.dobYear
    
    return `${year}-${month}-${day}`
  }

  // Build the payload for backend
  const buildApplicationPayload = () => {
    const employerTenure = calculateEmployerTenure()
    const residenceMonths = calculateMonthsFromStart(
      residenceData.moveInMonth,
      residenceData.moveInDay,
      residenceData.moveInYear
    )

    return {
      product_package_id: applicationIds.productPackageId || '',
      organization_id: applicationIds.organizationId || '',
      
      // Loan Information
      current_lienholder: loanData.currentLender || '',
      quotes_customer_reported_payoff: loanData.estimatedPayoff || '',
      quotes_customer_reported_payment: loanData.monthlyPayment || '',
      
      // Residence Information
      applicant_payment: residenceData.monthlyPayment || '',
      applicant_months: residenceMonths,
      applicant_own_rent: residenceData.ownershipStatus || 'RENT',
      
      // Employment/Income Information
      applicant_employer_employer: incomeData.currentEmployer || '',
      applicant_employer_employment_type: incomeData.employmentType || '',
      applicant_employer_income: incomeData.annualIncome || '',
      applicant_employer_income_frequency: 'Annually',
      applicant_employer_years: employerTenure.years,
      applicant_employer_months: employerTenure.months,
      
      // Personal Information
      applicant_dob: user?.dob || formatDOB(),
      applicant_ssn: user?.ssn || consentData.ssn || '',
      
      // Consent & Agreements
      applicant_pp: (consentData.ssn && agreeToDisclosure) ? 1 : 0, // Privacy Policy
      applicant_tcpa: consentData.agreeToDisclosure ? 1 : 0, // TCPA from consent page
      
      // Additional context
      ownership_status: ownershipData.ownershipStatus || '',
    }
  }

  const handleApply = async () => {
    if (!agreeToDisclosure) {
      setError('You must confirm that you have read and agree to the disclosure')
      return
    }

    setIsSubmitting(true)
    
    try {
      const payload = buildApplicationPayload()
      const response = await dispatch(submitRefiApplication(payload) as any)

      // check for successful response (adjust if your action returns differently)
      if (response?.loan_application_no) {
        history.push(`/users/${product_package?.user?.id}`)
      } else {
        setError('Unexpected response from server.')
      }
    } catch (err) {
      setError('Failed to submit application. Please try again.')
      setTimeout(() => {
          window.location.href = `/users/${product_package?.user?.id}`
      }, 4000);
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    history.push('/refiApp/financing/apply/consent')
  }

  return (
    <div className="disclosure-agreement-form" style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
      <div className="disclosure-agreement-form__content">
        <div className="disclosure-box" style={{
          border: '2px solid #007bff',
          borderRadius: '8px',
          padding: '30px',
          backgroundColor: '#f8f9fa',
          marginBottom: '20px'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '20px' }}>
            Read and Agree
          </h2>
          <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#555', marginBottom: '0' }}>
            By agreeing to submit this application, you acknowledge that you have read and agree to our lenders{' '}
            <a 
              href="https://gravitylending.com/terms-and-conditions/" 
              style={{ color: '#007bff', textDecoration: 'underline', fontWeight: '500' }}
              onClick={(e) => e.preventDefault()}
            >
              Terms and Conditions
            </a>
            {' '}and{' '}
            <a 
              href="https://gravitylending.com/privacy-policy/" 
              style={{ color: '#007bff', textDecoration: 'underline', fontWeight: '500' }}
              onClick={(e) => e.preventDefault()}
            >
              Privacy Policy
            </a>
            {' '}(Terms) that can be found at{' '}
            <strong style={{ fontWeight: '600' }}>GravityLending.com</strong>
            ; and that all the information provided is true, correct and complete and you grant permission for{' '}
            <strong style={{ fontWeight: '600' }}>Gravity Lending and/or its Lenders</strong>
            {' '}to obtain a copy of your credit report.
          </p>
          <p>
            By agreeing, you consent to receive customer care, 2FA, and 2-way conversational text messages from Blinker, its partners and Gravity Lending at the number provided, including messages sent by autodialer. Consent is not a condition of purchase. Message and data rates may apply. Message frequency varies. Unsubscribe at any time by replying STOP. Reply HELP for help.
          </p>
        </div>

        <div className="form__section" style={{ marginBottom: '30px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '12px',
            padding: '15px',
            backgroundColor: agreeToDisclosure ? '#e7f3ff' : 'transparent',
            borderRadius: '4px',
            border: error ? '1px solid #dc3545' : '1px solid transparent'
          }}>
            <input
              id="agreeToDisclosure"
              name="agreeToDisclosure"
              type="checkbox"
              checked={agreeToDisclosure}
              onChange={handleCheckboxChange}
              style={{ 
                width: '24px', 
                height: '24px', 
                cursor: 'pointer',
                marginTop: '2px',
                accentColor: '#007bff'
              }}
            />
            <label 
              htmlFor="agreeToDisclosure" 
              style={{ 
                cursor: 'pointer', 
                fontSize: '16px',
                lineHeight: '1.5',
                color: '#333',
                userSelect: 'none'
              }}
            >
              Confirm you have read and agree to the{' '}
              disclosure
            </label>
          </div>
          {error && (
            <p className="form__help-text text-error" style={{ marginTop: '8px', marginBottom: '0', fontSize: '14px' }}>
              {error}
            </p>
          )}
        </div>

        <div className="disclosure-agreement-form__actions">
          <button
            type="button"
            onClick={handleApply}
            disabled={isSubmitting}
            className="button button--primary button--full-width"
            style={{
              fontSize: '18px',
              padding: '16px',
              fontWeight: '600',
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Apply'}
          </button>
        </div>

        <div className="disclosure-agreement-form__navigation" style={{ marginTop: '20px', textAlign: 'left' }}>
          <button
            type="button"
            onClick={handleBack}
            className="button button--link-text"
            style={{ fontSize: '16px' }}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  )
}

DisclosureAgreementForm.displayName = 'Disclosure Agreement Form'

export default DisclosureAgreementForm
